/**
 * Date formatting options for consistent date string representation
 * @type {Object}
 */
const dateStringOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
};

/**
 * Converts interview data to form data format for the personal information form
 * @param {Object} interview - The interview object containing question answers and relationships
 * @param {Object} interview.questionAnswers - Object containing question type keys and answer objects
 * @param {Array} interview.relationships - Array of relationship objects with type and sourceOfBusiness properties
 * @returns {Object} Form data object with mapped properties for personal information form
 */
export function interviewToFormData(interview) {
    /**
     * Helper function to extract answer from interview question answers
     * @param {string} questionType - The type/key of the question
     * @returns {string|undefined} The answer string or undefined if not found
     */
    const answer = questionType => interview?.questionAnswers[questionType]?.answer;

    /**
     * Determines work arrangement based on interview answers
     * @returns {string|undefined} Work arrangement type: "Remote", "Hybrid", "InOffice", or undefined
     */
    const workArrangement = _ => {
        if (answer("WorkArrangementRemote") === "true") { return "Remote"; }
        if (answer("WorkArrangementHybrid") === "true") { return "Hybrid"; }
        if (answer("WorkArrangementInOffice") === "true") { return "InOffice"; }
    }

    const dateOfBirth = answer("DateOfBirth") ?
        new Date(answer("DateOfBirth")).toLocaleDateString("en-US", dateStringOptions) :
        null;

    const parentBranch = interview?.relationships?.find(x => x.type === "ParentBranch") ??
        interview?.relationships?.find(x => x.type === "ParentAgency");

    const isOffshoreResident = answer("OffshoreResident") === "true";
    // For offshore residents, default country to empty to show "Please Select"
    // Also normalize "US" to empty for offshore since US is excluded for them
    // For non-offshore residents, default to "US"
    const savedCountry = answer("ResidentialAddressCountry");
    let residentialCountry = "";
    if (isOffshoreResident) {
        if (savedCountry && savedCountry !== "US") {
            residentialCountry = savedCountry;
        }
    } else {
        residentialCountry = savedCountry || "US";
    }


    return {
        dateOfBirth: dateOfBirth,
        ssn: answer("Ssn"),
        ssnConfirm: answer("Ssn"),
        isOffshoreResident: isOffshoreResident,
        residentialAddress: {
            country: residentialCountry,
            streetAddressLineOne: answer("ResidentialAddressStreetLineOne"),
            streetAddressLineTwo: answer("ResidentialAddressStreetLineTwo"),
            zipCode: answer("ResidentialAddressZipCode"),
            city: answer("ResidentialAddressCity"),
            state: answer("ResidentialAddressState"),
            county: answer("ResidentialAddressCounty"),
        },
        businessInFlorida: answer("BusinessInFlorida"),
        businessInFloridaCounty: answer("FLCounty")?.split(",").map(s => s.trim()),
        timeAtAgency: answer("YearsWithAgency"),
        isLicensedProducer: answer("LicensedProducer"),
        producerNPN: answer("Npn"),
        workArrangement: workArrangement(),
        lobCommercial: answer("SoldLobCommercial") === "true",
        lobHealth: answer("SoldLobHealth") === "true",
        lobHome: answer("SoldLobHome") === "true",
        lobPersonal: answer("SoldLobPersonalAuto") === "true",
        lobOther: answer("SoldLobOther") === "true",
        otherLineOfBusiness: answer("OtherLinesOfBusiness"),
        parentBranchSob: parentBranch?.sourceOfBusiness,
    };
}

/**
 * Converts form data to question answers format for interview processing
 * @returns {Object} Question answers object with mapped properties for interview processing
 */
export function formDataToQuestionAnswers(formData) {
    /**
     * Helper function to map a value to question answer format if it exists
     * @param {string} key - The question key/type
     * @param {*} value - The value to map
     * @returns {Object|{}} Question answer object or empty object if value doesn't exist
     */
    const mapIfExists = (key, value) =>
        value !== null && value !== undefined
            ? { [key]: { answer: value.toString() } }
            : {};

    // Map workArrangement to three boolean fields
    const workArrangementRemote = formData.workArrangement === "Remote" ? "true" : "false";
    const workArrangementHybrid = formData.workArrangement === "Hybrid" ? "true" : "false";
    const workArrangementInOffice = formData.workArrangement === "InOffice" ? "true" : "false";

    const dateOfBirth = formData.dateOfBirth ?
        new Date(formData.dateOfBirth).toLocaleDateString("en-US", dateStringOptions) :
        null;

    // For offshore residents, don't default to "US" - use empty string or actual value
    const countryValue = formData.residentialAddress?.country ||
        (formData.isOffshoreResident ? "" : "US");

    return {
        ...mapIfExists("DateOfBirth", dateOfBirth),
        ...mapIfExists("Ssn", formData.ssn),
        ...mapIfExists("ResidentialAddressCountry", countryValue),
        ...mapIfExists("ResidentialAddressStreetLineOne", formData.residentialAddress?.streetAddressLineOne),
        ...mapIfExists("ResidentialAddressStreetLineTwo", formData.residentialAddress?.streetAddressLineTwo),
        ...mapIfExists("ResidentialAddressZipCode", formData.residentialAddress?.zipCode),
        ...mapIfExists("ResidentialAddressCity", formData.residentialAddress?.city),
        ...mapIfExists("ResidentialAddressState", formData.residentialAddress?.state),
        ...mapIfExists("ResidentialAddressCounty", formData.residentialAddress?.county),
        ...mapIfExists("BusinessInFlorida", formData.businessInFlorida),
        ...mapIfExists("FLCounty", formData.businessInFloridaCounty),
        ...mapIfExists("YearsWithAgency", formData.timeAtAgency),
        ...mapIfExists("LicensedProducer", formData.isLicensedProducer),
        ...mapIfExists("Npn", formData.producerNPN),
        WorkArrangementRemote: { answer: workArrangementRemote },
        WorkArrangementHybrid: { answer: workArrangementHybrid },
        WorkArrangementInOffice: { answer: workArrangementInOffice },
        ...mapIfExists("SoldLobCommercial", formData.lobCommercial),
        ...mapIfExists("SoldLobHealth", formData.lobHealth),
        ...mapIfExists("SoldLobHome", formData.lobHome),
        ...mapIfExists("SoldLobPersonalAuto", formData.lobPersonal),
        ...mapIfExists("SoldLobOther", formData.lobOther),
        ...mapIfExists("OtherLinesOfBusiness", formData.otherLineOfBusiness)
    };
}
